/**
 * Makes the earned discount visible, and consistent, everywhere it is quoted.
 *
 * A/B pdp_quiz_unlock_2608, arm B only.
 *
 * ── The problem ─────────────────────────────────────────────────────────────
 *
 * The earned discount arrives in two halves that behave completely differently.
 *
 * The ladder — 17/24/31% — is a selling plan, so it is a real price on a real
 * line item. Shopify renders it everywhere without being asked.
 *
 * The SMS bonus is not a price. It is a discount code held against the cart
 * session, and a cart-level discount only appears where a surface chooses to
 * render `cart_level_discount_applications`. The theme's /cart page does.
 * UpCart's drawer and the product page do not. So a shopper promised 22% saw
 * 17% on the page, 17% in the drawer, and the rest only at the checkout.
 *
 * Every surface it touches is listed in paint() below.
 *
 * ── Where the numbers come from ─────────────────────────────────────────────
 *
 * The percentage comes from the cart's own discount application, or — when
 * there is no cart yet — from the cookie the quiz writes at the phone step,
 * which it writes only once the code has been applied to the session. With
 * neither, nothing is drawn and the page keeps saying 17%, which is then true.
 *
 * The unlock cookie cannot be used for this: it is '=1', written at the email
 * step, and records that the quiz was finished rather than what was won.
 *
 * Money comes from the cart every time, and the cart is re-read whenever the
 * drawer changes. A figure painted once goes stale the first time somebody
 * changes a quantity, and a stale total beside a live one is worse than no
 * total at all.
 */
(function () {
  var HTML = document.documentElement;
  if (HTML.getAttribute('data-ab3') !== 'b') return;

  /* Whole dollars stay whole: "$48" beside "$45.60" is how the rest of the page
     writes prices, and "$48.00" next to it looks like a different system. */
  function money(cents) {
    var n = Number(cents) || 0;
    return '$' + (n % 100 === 0 ? String(n / 100) : (n / 100).toFixed(2));
  }

  function parseMoney(text) {
    var n = parseFloat(String(text).replace(/[$,]/g, ''));
    return isFinite(n) ? Math.round(n * 100) : NaN;
  }

  var MONEY_RE = /\$\d{1,3}(?:,\d{3})*(?:\.\d{2})?/;

  function visible(el) {
    return el && el.offsetParent !== null;
  }

  /* ── the cart ─────────────────────────────────────────────────────────── */

  /* line_price, not final_line_price. Shopify uses `final_*` for automatic
     discounts and scripts and leaves code allocations out of it, so on this
     cart final_line_price reads 4800 while line_price reads 4560 — the second
     is what the shopper is charged. */
  function readCart(done) {
    try {
      window
        .fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
        .then(function (r) { return r.json(); })
        .then(function (c) {
          var apps = (c && c.cart_level_discount_applications) || [];
          var saved = 0;
          for (var i = 0; i < apps.length; i++) saved += Number(apps[i].total_allocated_amount) || 0;

          var sub = null;
          for (var j = 0; j < (c.items || []).length; j++) {
            if (c.items[j].selling_plan_allocation) { sub = c.items[j]; break; }
          }
          done({
            pct: apps.length ? Math.round(Number(apps[0].value) || 0) : 0,
            saved: saved,
            cartTotal: Number(c.total_price) || 0,
            /* The unit price, not the line total. original_line_price scales
               with quantity, so at two bottles it read $96 and matched no card
               at all — which silently took the banner and the restruck price
               with it, because both hang off finding one. */
            unitWas: sub ? Number(sub.original_price) : null,
            /* Everything off the order, which is the figure the checkout
               prints as TOTAL SAVINGS: original_total_price less total_price,
               which Shopify hands over already worked out. It counts the
               second bottle's $37, the bonus's $2.40 and the gifts at their
               list prices — the gifts being most of it, and the part no line
               in the drawer adds up for her. */
            savedAll: Number(c.total_discount) || 0,
            /* Quantity and unit charge, because the drawer prints line totals
               rather than unit prices the moment there is more than one. */
            qty: sub ? Number(sub.quantity) || 1 : 1,
            unitNow: sub ? Number(sub.price) : null
          });
        })['catch'](function () { done(null); });
    } catch (e) { done(null); }
  }

  /* The bonus the quiz recorded, for when there is no cart to read it from.
     Percentage only — it says what this shopper is entitled to, which is a fact
     about them and does not depend on a cart existing. It never sets a price. */
  function cookieBonus() {
    try {
      var m = document.cookie.match(/(^|;)\s*_fleur_quiz_bonus\s*=\s*([^;]+)/);
      var v = m ? Math.round(Number(m[2])) : 0;
      return v > 0 && v <= 20 ? v : 0;
    } catch (e) { return 0; }
  }

  /* The plan card the cart's subscription line belongs to.

     Matched on the first-payment figure Liquid rendered, not on which card is
     selected: the discount belongs to the plan that was bought, and a shopper
     browsing the 6-month card after subscribing monthly must not be shown the
     6-month's numbers as though they were theirs. */
  function cardForCart(d) {
    if (!d || d.unitWas == null) return null;
    var was = money(d.unitWas);
    var cards = document.querySelectorAll('.custom-variant-box .variant-card');
    for (var i = 0; i < cards.length; i++) {
      var line = cards[i].querySelector('.ab3-unlocked');
      var orig = line && (line.getAttribute('data-orig-text') || line.textContent);
      var m = orig && String(orig).match(new RegExp('first payment\\s*(' + MONEY_RE.source + ')'));
      if (m && m[1] === was) return cards[i];
    }
    return null;
  }

  function unlockPct(card) {
    var panel = card && card.querySelector('.plan_offer[data-plan-unlock]');
    var v = panel ? Number(panel.getAttribute('data-plan-unlock')) : 0;
    return v > 0 ? v : 0;
  }

  /* ── the product page ─────────────────────────────────────────────────── */

  /* ── the countdown ────────────────────────────────────────────────────
     Five minutes from the moment the earned panel is first drawn, held in
     sessionStorage so a refresh or a walk through the site resumes the same
     clock rather than restarting it. A countdown that resets on every page view
     is not a countdown, and a shopper who reloads once notices immediately.

     Session-scoped, per the brief: it lasts as long as the tab does and a
     genuine return visit starts clean.

     What it does NOT do is take the discount away. The unlock is a 90-day
     cookie and a server-side record, and neither expires in five minutes, so at
     zero the line drops the clock and keeps the offer rather than claiming
     something has lapsed when nothing has. */
  var TIMER_MS = 5 * 60 * 1000;
  var UNTIL = '_fleur_quiz_until';
  var UNLOCK = '_fleur_quiz_unlock';
  var BONUS = '_fleur_quiz_bonus';

  function readCookie(name) {
    try {
      var m = document.cookie.match('(^|;)\\s*' + name + '\\s*=\\s*([^;]+)');
      return m ? m.pop() : null;
    } catch (e) { return null; }
  }

  function writeCookie(name, value, days) {
    try {
      var d = new Date();
      d.setTime(d.getTime() + days * 864e5);
      document.cookie = name + '=' + value + ';expires=' + d.toUTCString() +
        ';path=/;SameSite=Lax';
    } catch (e) {}
  }

  function dropCookie(name) {
    try {
      document.cookie = name + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;SameSite=Lax';
    } catch (e) {}
  }

  /* A cookie, not sessionStorage. The deadline is now the offer itself rather
     than a decoration on it, so it has to be legible to snippets/ab-assign.liquid
     before the first paint — otherwise someone who closed the tab with two
     minutes left would come back an hour later to the earned price.

     Long-lived, holding the moment it lapses. If the cookie itself expired at
     the deadline, its absence would be indistinguishable from a completer who
     predates this window, and those shoppers are deliberately grandfathered. */
  function deadline() {
    var raw = readCookie(UNTIL);
    var at = raw ? Number(raw) : 0;
    if (at > 0) return at;
    at = +new Date() + TIMER_MS;
    writeCookie(UNTIL, String(at), 90);
    return at;
  }

  function remaining() {
    return Math.max(0, Math.ceil((deadline() - +new Date()) / 1000));
  }

  function clock(secs) {
    var m = Math.floor(secs / 60);
    var s = secs % 60;
    return m + ':' + (s < 10 ? '0' : '') + s;
  }

  /* Liquid renders "Your 17% is applied", which is the ladder and nothing else.
     With the bonus earned it becomes both halves, named as something earned. */
  function paintPanel(bonus) {
    var nodes = document.querySelectorAll('.plan_offer--earned .plan_offer__done');
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];

      /* The ladder, read once from what Liquid rendered and then remembered —
         after the first repaint this element carries two percentages and the
         first is no longer the ladder. */
      var base = el.getAttribute('data-base-pct');
      if (!base) {
        var head = el.firstChild && el.firstChild.nodeType === 3
          ? el.firstChild.nodeValue : (el.textContent || '');
        var m = String(head).match(/(\d+)\s*%/);
        base = m ? m[1] : '';
        el.setAttribute('data-base-pct', base);
      }
      if (!base) continue;

      /* The headline comes from Liquid's own figure on this card, so the panel,
         the flag and the quiz cannot state three different numbers. */
      /* With the bonus gone the headline is the ladder alone. data-plan-unlock
         is the combined figure and would keep claiming 22% after the 5% had
         lapsed. */
      var card = el.closest ? el.closest('.variant-card') : null;
      var total = bonus > 0 ? (unlockPct(card) || Number(base) + bonus) : Number(base);

      /* No money here. The panel follows whichever plan is selected, and each
         card's payment line already states that card's own first payment. */
      var left = bonus > 0 ? remaining() : 0;
      var html =
        'Quiz complete: ' + total + '% discount activated' +
        (left > 0
          ? ' for <span data-fleur-countdown>' + clock(left) + '</span>'
          : '') +
        '<span class="plan_offer__split">' +
        (bonus > 0
          ? base + '% off every delivery, ' + bonus + '% more on your first order'
          : 'applied to every delivery') +
        '</span>';
      if (el.innerHTML !== html) el.innerHTML = html;
    }
  }

  /* "first payment $48, then $48 every 1 month" is rendered by Liquid from the
     selling plan, which knows nothing about a cart-level code.

     Every plan, not only the one in the cart: the bonus is a percentage code
     targeting all items, so it takes the same proportion off whichever plan is
     bought, and each card should state what that card would actually cost. */
  function paintPayment(bonus) {
    var nodes = document.querySelectorAll('.custom-variant-box .ab3-unlocked');
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      var orig = el.getAttribute('data-orig-text');
      if (orig === null) {
        orig = el.textContent.replace(/\s+/g, ' ').trim();
        el.setAttribute('data-orig-text', orig);
      }
      /* A comma is only a thousands separator when three digits follow it.
         [\d,]+ swallowed the comma after "first payment $48," and captured
         "$48,", which matched nothing and silently left every line alone. */
      var m = orig.match(new RegExp('first payment\\s*(' + MONEY_RE.source + ')'));
      var full = m ? parseMoney(m[1]) : NaN;
      var want = orig;
      if (m && isFinite(full) && bonus > 0) {
        var first = Math.round((full * (100 - bonus)) / 100);
        if (first < full) want = orig.replace(m[0], 'first payment ' + money(first));
      }
      if (el.textContent !== want) el.textContent = want;
    }
  }

  /* The announcement bar's receipt. Liquid ships the floor, because the bar is
     site-wide and the server has no selected plan to read; here there is one. */
  /* Same reasoning as the panel: once the bonus has lapsed the bar states the
     ladder, read from the figure Liquid rendered on this card. */
  function paintBar(card, bonus) {
    var pct = unlockPct(card);
    if (bonus <= 0) {
      var done = card && card.querySelector('.plan_offer--earned .plan_offer__done');
      var base = done && Number(done.getAttribute('data-base-pct'));
      pct = base > 0 ? base : 0;
    }
    if (!pct) return;
    var nodes = document.querySelectorAll('.ab3-bar-earned [data-ab3-earned-pct]');
    for (var i = 0; i < nodes.length; i++) {
      if (nodes[i].textContent !== String(pct)) nodes[i].textContent = String(pct);
    }
  }

  /* ── the drawer ───────────────────────────────────────────────────────── */

  /* It lives in a shadow root, so nothing in it is reachable from an ordinary
     selector — a query from `document` searches the wrong tree entirely. Every
     node has to be found by walking into each shadowRoot in turn. */
  function eachShadow(root, fn) {
    var all;
    try { all = root.querySelectorAll('*'); } catch (e) { return; }
    for (var i = 0; i < all.length; i++) {
      if (all[i].shadowRoot) { fn(all[i].shadowRoot); eachShadow(all[i].shadowRoot, fn); }
    }
  }

  /* Found once and kept. Locating it means walking every element on the page
     and then every element inside every shadow root, which is not something to
     do on each repaint — and repaints are frequent, because the drawer
     re-renders for its own reasons and the page mutates constantly on its own
     (live-shopper counters, carousels, lazy images). */
  var drawerRoot = null;

  function getDrawerRoot() {
    if (drawerRoot && drawerRoot.host && drawerRoot.host.isConnected) return drawerRoot;
    drawerRoot = null;
    eachShadow(document, function (root) {
      if (drawerRoot) return;
      if (root.querySelector('ul[class*="cart-items"], [class*="product-tile"]')) drawerRoot = root;
    });
    return drawerRoot;
  }

  /* Leaf nodes whose entire text is one price. Leaves only: a container holding
     "$68$48" would otherwise be rewritten as a whole and both prices lost. */
  function moneyLeaves(scope) {
    var out = [];
    var els = scope.querySelectorAll('span,div,p,b,s,del');
    for (var i = 0; i < els.length; i++) {
      if (els[i].children.length) continue;
      var t = (els[i].textContent || '').trim();
      if (t && new RegExp('^' + MONEY_RE.source + '$').test(t)) out.push(els[i]);
    }
    return out;
  }

  function struck(el) {
    try { return /line-through/.test(getComputedStyle(el).textDecorationLine || ''); }
    catch (e) { return false; }
  }

  /* The headline the drawer opens with.

     The first price in the panel is the line item's $48, and a shopper promised
     22% reads that as having been given 17% and no more — the bonus appears on
     no line. So the amount actually charged is stated before anything else, and
     everything below can be read as detail rather than as a contradiction. */
  /* Everything this file added to the drawer, removed. Called when the bonus
     goes: with nothing left to explain, a banner announcing a discount that has
     lapsed is worse than no banner. The restruck price stays — that is the
     ladder, and the ladder has not gone anywhere. */
  function cleanDrawer(root) {
    var el = root.querySelector('[data-fleur-total]');
    if (el && el.parentNode) el.parentNode.removeChild(el);
    el = root.querySelector('[data-fleur-bonus]');
    if (el && el.parentNode) el.parentNode.removeChild(el);
  }

  function paintBanner(root, d, card) {
    var list = root.querySelector('ul[class*="cart-items"]');
    if (!list || !list.parentNode) return;

    var pct = card ? unlockPct(card) : 0;
    var ladder = pct > d.pct ? pct - d.pct : 0;

    var box = root.querySelector('[data-fleur-total]');
    if (!box) {
      box = document.createElement('div');
      box.setAttribute('data-fleur-total', '1');
      box.style.cssText =
        'padding:14px 20px 13px;background:#fbf3f1;border-bottom:1px solid #f1e3e0;font-family:inherit';
      list.parentNode.insertBefore(box, list);
    }
    /* Says what the lines cannot.

       Shopify keeps code allocations out of final_line_price, so the drawer
       prints the subscription at $48 however much has actually come off it —
       there is no way to make that line read $45.60. So the total is stated
       first, and then the reason the arithmetic above it does not add up,
       because a shopper who spots the gap and is not told about it concludes
       the bonus did not work. */
    var html =
      '<div style="font-size:19px;font-weight:700;line-height:1.15;color:#812221;' +
      'letter-spacing:-.3px">' + money(d.cartTotal) + ' today</div>' +
      '<div style="margin-top:4px;font-size:12px;line-height:1.4;color:#4a4340">' +
      (d.saved > 0
        ? 'Includes your ' + d.pct + '% bonus &mdash; ' + money(d.saved) +
          ' off. Prices below are shown before it.'
        : (pct ? 'Your ' + pct + '% quiz discount is applied'
               : 'Your quiz discount is applied')) +
      (ladder ? ' &middot; ' + ladder + '% off every delivery' : '') +
      '</div>';
    if (box.innerHTML !== html) box.innerHTML = html;
  }

  /* The drawer strikes compare_at_price — $68, what one bottle costs bought
     outright — against the plan's $48. That is the same mistake the product
     card was making: it credits the quiz with a saving it did not produce, and
     it is the number sitting right beside the $48 the banner exists to explain.
     Restruck against what this plan costs without the quiz, so both surfaces
     anchor to the same figure.

     Scoped to the tile holding the subscription. The free gifts legitimately
     strike $20 and $30 against $0, and rewriting those would be vandalism. */
  function paintCompare(root, d, card) {
    var was = [].filter.call(card.querySelectorAll('.original_price'), visible)[0];
    var wasUnit = was ? parseMoney(was.textContent) : NaN;
    if (!isFinite(wasUnit) || d.unitNow == null) return;

    /* Multiplied by the quantity, because the drawer prints the line total: at
       two bottles it shows $136 against $96, not $68 against $48. Comparing
       unit prices there matched nothing and left the wrong anchor standing. */
    var nowText = money(d.unitNow * d.qty);
    var wasText = money(wasUnit * d.qty);
    if (nowText === wasText) return;

    var tiles = root.querySelectorAll('[class*="product-tile"]');
    for (var i = 0; i < tiles.length; i++) {
      var leaves = moneyLeaves(tiles[i]);
      var isSub = false;
      for (var j = 0; j < leaves.length; j++) {
        if (leaves[j].textContent.trim() === nowText && !struck(leaves[j])) isSub = true;
      }
      if (!isSub) continue;
      for (var k = 0; k < leaves.length; k++) {
        var el = leaves[k];
        if (!struck(el)) continue;
        if (el.textContent.trim() === wasText) continue;
        el.textContent = wasText;
      }
    }
  }

  /* There is no subtotal row to attach to: the whole footer is the checkout
     button. The row goes directly above it, where it explains the difference
     between the line item and the amount on the button. */
  function footerButton(root) {
    /* By class, but not the first match — the app renders more than one of
       these and the first is empty. Taking it meant every lookup landed on a
       container with no label, which the checkout total's own guard then
       rejected, so that rewrite never ran at all. */
    var target = null;
    var byClass = root.querySelectorAll('[class*="checkout-button-container"]');
    for (var c = 0; c < byClass.length; c++) {
      if (/^check\s?out\b/i.test((byClass[c].textContent || '').replace(/\s+/g, ' ').trim())) {
        target = byClass[c];
        break;
      }
    }
    if (!target) {
      var els = root.querySelectorAll('div,button,a');
      for (var i = 0; i < els.length; i++) {
        var t = (els[i].textContent || '').replace(/\s+/g, ' ').trim();
        if (t.length <= 30 && /^check\s?out\b/i.test(t)) { target = els[i]; break; }
      }
    }
    if (!target) return null;
    while (target.parentNode && target.parentNode !== root &&
           (target.parentNode.textContent || '').replace(/\s+/g, ' ').trim().length ===
           (target.textContent || '').replace(/\s+/g, ' ').trim().length) {
      target = target.parentNode;
    }
    return target;
  }

  function paintBonusRow(root, d) {
    var target = footerButton(root);
    if (!target || !target.parentNode) return;
    var row = root.querySelector('[data-fleur-bonus]');
    if (!row) {
      row = document.createElement('div');
      row.setAttribute('data-fleur-bonus', '1');
      row.style.cssText =
        'display:flex;justify-content:space-between;align-items:baseline;gap:12px;' +
        'padding:10px 20px 2px;font-size:13px;line-height:1.3;color:#812221;font-weight:700';
      target.parentNode.insertBefore(row, target);
    }
    /* The whole saving, not one line of it.

       This said "5% quiz bonus · first order  −$2.40", which is the smallest
       true number available and the least of what she is getting: the same
       order saves $37 on the second bottle and takes $50 of gifts off the top.
       The bonus still needs explaining — that is the banner's job, because it
       is the piece that makes the line items look wrong — but the last thing
       above the button should be the size of the win, not its footnote.

       Same figure the checkout prints, and deliberately so: a shopper who
       reads $89.39 here and $89.39 on the next page has been told the truth
       twice, which is worth more than either statement alone. */
    var html = d.savedAll > 0
      ? '<span style="letter-spacing:.04em">TOTAL SAVINGS</span>' +
        '<span>' + money(d.savedAll) + '</span>'
      : '<span>' + d.pct + '% quiz bonus &middot; first order</span>' +
        '<span>&minus;' + money(d.saved) + '</span>';
    if (row.innerHTML !== html) row.innerHTML = html;
  }

  /* The button rounds to whole dollars — "Checkout • $46" under a banner
     reading "$45.60 today". Both are the same charge and neither is wrong, but
     two figures for one number is exactly the doubt this whole file exists to
     remove. Set from the cart, which is re-read on every change, so toggling
     shipping protection or a quantity moves it rather than stranding it. */
  function paintCheckoutTotal(root, d) {
    var target = footerButton(root);
    if (!target) return;
    if (!/^check\s?out/i.test((target.textContent || '').replace(/\s+/g, ' ').trim())) return;

    var want = money(d.cartTotal);

    /* Text nodes, not elements. "Checkout • $46" is not the whole text of any
       childless element — the label and the amount are separate nodes inside a
       div that also holds an icon — so a scan for elements whose entire text is
       the price found nothing and silently did nothing. Walking the text nodes
       finds the one carrying the amount wherever the app chose to put it. */
    var walker = document.createTreeWalker(target, NodeFilter.SHOW_TEXT, null, false);
    var node;
    while ((node = walker.nextNode())) {
      var t = node.nodeValue;
      if (!t) continue;
      var m = t.match(MONEY_RE);
      if (!m || m[0] === want) continue;
      node.nodeValue = t.replace(m[0], want);
    }
  }

  /* ── painting ─────────────────────────────────────────────────────────── */

  function paint(d) {
    if (HTML.getAttribute('data-quiz-unlock') !== 'yes') return;
    /* Zero once the window has closed, and the panel then states the ladder on
       its own rather than falling silent — the discount is still applied, and a
       page that stops mentioning it reads as though it had gone too. */
    var bonus = expired ? 0 : (d && d.pct ? d.pct : cookieBonus());

    var card = cardForCart(d);
    paintPanel(bonus);
    paintPayment(bonus);
    if (card) paintBar(card, bonus);

    /* Everything below quotes money from the cart, so none of it runs without
       one. An entitlement can be stated from a cookie; an amount cannot. */
    var root = getDrawerRoot();
    if (!root) return;
    if (!d || !d.pct || !bonus) { cleanDrawer(root); return; }
    /* The banner no longer waits on a product card.

       It quotes the cart — a total and an amount saved — and only borrowed the
       card for the ladder percentage beside it. But cardForCart matches a
       variant card on the product page by its "first payment $48" text, and
       when that match fails, which it does whenever the drawer is open over
       anything but the right card, the one line explaining why the drawer's
       prices are lower than they look disappeared with it. The ladder is the
       optional half; the explanation is not. */
    paintBanner(root, d, card);
    if (card) paintCompare(root, d, card);
    paintBonusRow(root, d);
    paintCheckoutTotal(root, d);
  }

  var pending = null;
  var inFlight = false;
  var lastRead = 0;
  var observer = null;

  /* Painting is deaf to itself.

     Every write here is already conditional, so a steady state produces no
     mutations at all — but the first paint after a change does, and hearing it
     put the page in a loop: paint, mutate, wake, re-read the cart, paint. On an
     idle product page that was 29 cart requests every fifteen seconds, each one
     followed by a walk of the whole document and every shadow root inside it,
     calling getComputedStyle on every price it found. The page did not error;
     it just never stopped working. */
  function paintQuietly(d) {
    if (observer) observer.disconnect();
    try {
      paint(d);
    } finally {
      if (observer) observer.observe(document.documentElement, { childList: true, subtree: true });
    }
  }

  function refresh() {
    if (inFlight) return;
    /* A floor between reads, so a burst of unrelated DOM churn — a carousel, a
       lazy image, someone else's widget — cannot turn into a burst of
       requests. */
    var now = +new Date();
    if (now - lastRead < 800) {
      if (!pending) {
        pending = window.setTimeout(function () { pending = null; refresh(); }, 800);
      }
      return;
    }
    lastRead = now;
    inFlight = true;
    readCart(function (d) {
      inFlight = false;
      lastCart = d;
      paintQuietly(d);
    });
  }

  /* The drawer is rendered on open and re-rendered on every quantity change,
     add-on toggle and upsell — each of which also changes the totals. So the
     cart is re-read rather than the old figures repainted: a banner that keeps
     saying $45.60 after someone adds a second bottle is worse than none.

     Debounced, and every paint is a no-op when nothing has changed, so the
     mutations this causes itself settle after one pass instead of looping. */
  var lastCart = null;

  /* Repaint from what is already known. The drawer re-renders on its own and
     throws these nodes away when it does, so they have to go back — but nothing
     about the cart has changed, and asking the server again was the whole
     problem: unrelated page churn was being read as a reason to re-read the
     cart, at roughly a request per second, forever. */
  function repaint() {
    if (lastCart === null) return;
    paintQuietly(lastCart);
  }

  function watch() {
    if (!window.MutationObserver) return;
    observer = new MutationObserver(function () {
      if (pending) return;
      pending = window.setTimeout(function () {
        pending = null;
        repaint();
      }, 400);
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });
  }

  /* The cart is re-read when the cart is written to, which is the only thing
     that can change the numbers. Every path the theme and the apps use goes
     through one of these, and wrapping fetch catches them all without hooking
     each button. */
  function watchCart() {
    var native = window.fetch;
    if (typeof native !== 'function') return;
    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var writes = /\/cart\/(add|change|update|clear)(\.js)?/.test(url);
      var out = native.apply(this, arguments);
      if (writes && out && typeof out.then === 'function') {
        out.then(function (r) { window.setTimeout(refresh, 120); return r; },
                 function (e) { throw e; });
      }
      return out;
    };
  }

  /* Only the digits are rewritten, once a second, with the observer deaf to it —
     a full repaint every second would put the page back where it was before the
     loop was closed. The last rendered value is remembered so a second with no
     visible change writes nothing at all. */
  var shown = null;

  /* Time's up.

     The window is the offer, not a badge on it, so this takes the offer away:
     the unlock is dropped, the attribute comes off <html> and the arm rules
     revert the whole page on the spot — plan prices back to $58/$174/$348, the
     flags back to the invitation, the announcement bar back to "complete quiz
     for 22% off your order". No reload, and nothing left claiming a discount
     that has lapsed.

     The bonus code goes with it. Leaving it would revert the price on screen
     while the checkout quietly still took 5% off, which is the same mismatch
     this whole file exists to close — only pointing the other way.

     What is NOT touched is a line already in the cart. Someone who added the
     subscription while the offer stood keeps the plan they chose; the window
     governs what is on offer, not what has already been taken. */
  var expired = false;

  function expire() {
    if (expired) return;
    expired = true;

    /* The bonus only. The unlock cookie stays: the ladder was earned by
       answering thirteen questions and it stays earned, and taking it back
       would send the shopper through a questionnaire they had already completed
       to get it again. What the clock takes is the 5% that was conditional on
       acting quickly, which is the only part urgency was ever attached to. */
    dropCookie(BONUS);
    HTML.setAttribute('data-quiz-expired', 'yes');

    /* Repaint at the ladder rate: the panel loses its clock and its bonus line,
       the payment lines go back to the plan price, and the drawer's bonus row
       and headline come off. */
    paintPanel(0);
    paintPayment(0);
    var root = getDrawerRoot();
    if (root) cleanDrawer(root);

    /* The only way to clear a cart-level code from the storefront. Sending it
       through the unwrapped fetch would be tidier, but this one should be seen
       by the cart hook: the totals have just changed and the drawer needs to be
       repainted from the new ones. */
    try {
      window.fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ discount: '' })
      })['catch'](function () {});
    } catch (e) {}
  }

  function tick() {
    if (remaining() <= 0) { expire(); return; }
    var nodes = document.querySelectorAll('[data-fleur-countdown]');
    if (!nodes.length) return;
    var left = remaining();
    var text = clock(left);
    if (text === shown) return;
    shown = text;
    if (observer) observer.disconnect();
    try {
      for (var i = 0; i < nodes.length; i++) nodes[i].textContent = text;
    } finally {
      if (observer) observer.observe(document.documentElement, { childList: true, subtree: true });
    }
  }

  var started = false;

  function start() {
    /* Not yet unlocked when this page rendered.

       That used to end it: the attribute is written server-side from the
       cookie, so a shopper who took the quiz on this very page — which is now
       the only way she takes it — finished it holding a page that still said
       she had not. Nothing repainted until she reloaded, so the earned prices,
       the countdown and the banner were all missing from the one moment they
       were earned in.

       So instead of giving up, wait to be told. The quiz sets the attribute
       and fires this the moment the unlock is written. */
    if (HTML.getAttribute('data-quiz-unlock') !== 'yes') {
      document.addEventListener('quiz:unlocked', function () { start(); });
      return;
    }
    if (started) return;
    started = true;
    watchCart();
    refresh();
    watch();
    window.setInterval(tick, 1000);
    document.addEventListener('click', function (e) {
      if (e.target && e.target.closest && e.target.closest('.variant-card')) {
        window.setTimeout(refresh, 80);
      }
    });
    document.addEventListener('cart:refresh', refresh);
    /* And once more a beat later. Shopify recalculates a cart after the write
       that changed it, so a read fired on the event itself can still answer
       with the arithmetic from before. */
    document.addEventListener('cart:refresh', function () {
      window.setTimeout(refresh, 700);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
